import { useState } from 'react'
import BulkActionsBar from '../components/BulkActionsBar'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { persistDeletedVersion } from '../lib/lumiAppendOnly'
import { gerarCodigo, formatCurrency } from '../utils'
import type { AlocacaoColaborador, CargoFuncao, FaixaCusto, Colaborador } from '../types'

type Tab = 'cargos' | 'faixas' | 'colaboradores' | 'alocacoes'

export default function Colaboradores() {
  const {
    cargos, setCargos, faixasCusto, setFaixasCusto, colaboradores, setColaboradores,
    alocacoesColaboradores, setAlocacoesColaboradores, obras, centrosCusto, categorias, addLog,
  } = useApp()
  const [tab, setTab] = useState<Tab>('colaboradores')
  const [modal, setModal] = useState<'cargo' | 'faixa' | 'colaborador' | 'alocacao' | null>(null)
  const [editing, setEditing] = useState<any>(null)
  const [toast, setToast] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [colaboradoresPage, setColaboradoresPage] = useState(1)
  const [alocacoesPage, setAlocacoesPage] = useState(1)
  const [cargosPage, setCargosPage] = useState(1)
  const [faixasPage, setFaixasPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedColaboradores, setSelectedColaboradores] = useState<string[]>([])
  const [selectedAlocacoes, setSelectedAlocacoes] = useState<string[]>([])
  const [selectedCargos, setSelectedCargos] = useState<string[]>([])
  const [selectedFaixas, setSelectedFaixas] = useState<string[]>([])

  const [cargoForm, setCargoForm] = useState<{ nome: string; descricao: string; tipo_vinculacao_padrao: CargoFuncao['tipo_vinculacao_padrao']; status: CargoFuncao['status'] }>({ nome: '', descricao: '', tipo_vinculacao_padrao: 'CLT', status: 'ATIVO' })
  const [faixaForm, setFaixaForm] = useState<{ cargo_id: string; descricao: string; tipo_vinculacao: FaixaCusto['tipo_vinculacao']; salario_base_referencia: number; custo_pj_referencia: number; encargos_referencia: number; beneficios_referencia: number; status: FaixaCusto['status'] }>({ cargo_id: '', descricao: '', tipo_vinculacao: 'CLT', salario_base_referencia: 0, custo_pj_referencia: 0, encargos_referencia: 0, beneficios_referencia: 0, status: 'ATIVA' })
  const [colForm, setColForm] = useState<{ nome_completo: string; cpf: string; tipo_vinculacao: Colaborador['tipo_vinculacao']; cargo: string; cargo_id: string; faixa_custo_id: string; salario_base: number; custo_pj: number; encargos_mensais: number; beneficios_mensais: number; data_admissao: string; status: Colaborador['status']; observacoes: string }>({ nome_completo: '', cpf: '', tipo_vinculacao: 'CLT', cargo: '', cargo_id: '', faixa_custo_id: '', salario_base: 0, custo_pj: 0, encargos_mensais: 0, beneficios_mensais: 0, data_admissao: '', status: 'ATIVO', observacoes: '' })
  const [alocForm, setAlocForm] = useState({ colaborador_id: '', obra_id: '', centro_custo_id: '', categoria_orcamentaria_id: '', competencia: new Date().toISOString().slice(0, 7), percentual_alocacao: 100, observacoes: '' })

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000) }

  function openNewModal(nextModal: 'cargo' | 'faixa' | 'colaborador' | 'alocacao') {
    setEditing(null)
    setErrors({})
    if (nextModal === 'cargo') setCargoForm({ nome: '', descricao: '', tipo_vinculacao_padrao: 'CLT', status: 'ATIVO' })
    if (nextModal === 'faixa') setFaixaForm({ cargo_id: '', descricao: '', tipo_vinculacao: 'CLT', salario_base_referencia: 0, custo_pj_referencia: 0, encargos_referencia: 0, beneficios_referencia: 0, status: 'ATIVA' })
    if (nextModal === 'colaborador') setColForm({ nome_completo: '', cpf: '', tipo_vinculacao: 'CLT', cargo: '', cargo_id: '', faixa_custo_id: '', salario_base: 0, custo_pj: 0, encargos_mensais: 0, beneficios_mensais: 0, data_admissao: '', status: 'ATIVO', observacoes: '' })
    if (nextModal === 'alocacao') setAlocForm({ colaborador_id: '', obra_id: '', centro_custo_id: '', categoria_orcamentaria_id: '', competencia: new Date().toISOString().slice(0, 7), percentual_alocacao: 100, observacoes: '' })
    setModal(nextModal)
  }

  function openEditCargo(cargo: CargoFuncao) {
    setEditing(cargo)
    setCargoForm({
      nome: cargo.nome,
      descricao: cargo.descricao || '',
      tipo_vinculacao_padrao: cargo.tipo_vinculacao_padrao,
      status: cargo.status,
    })
    setErrors({})
    setModal('cargo')
  }

  function openEditFaixa(faixa: FaixaCusto) {
    setEditing(faixa)
    setFaixaForm({
      cargo_id: faixa.cargo_id,
      descricao: faixa.descricao,
      tipo_vinculacao: faixa.tipo_vinculacao,
      salario_base_referencia: faixa.salario_base_referencia,
      custo_pj_referencia: faixa.custo_pj_referencia,
      encargos_referencia: faixa.encargos_referencia,
      beneficios_referencia: faixa.beneficios_referencia,
      status: faixa.status,
    })
    setErrors({})
    setModal('faixa')
  }

  function openEditColaborador(colaborador: Colaborador) {
    setEditing(colaborador)
    setColForm({
      nome_completo: colaborador.nome_completo,
      cpf: colaborador.cpf,
      tipo_vinculacao: colaborador.tipo_vinculacao,
      cargo: colaborador.cargo,
      cargo_id: colaborador.cargo_id,
      faixa_custo_id: colaborador.faixa_custo_id,
      salario_base: colaborador.salario_base,
      custo_pj: colaborador.custo_pj,
      encargos_mensais: colaborador.encargos_mensais,
      beneficios_mensais: colaborador.beneficios_mensais,
      data_admissao: colaborador.data_admissao,
      status: colaborador.status,
      observacoes: colaborador.observacoes || '',
    })
    setErrors({})
    setModal('colaborador')
  }

  function openEditAlocacao(alocacao: AlocacaoColaborador) {
    setEditing(alocacao)
    setAlocForm({
      colaborador_id: alocacao.colaborador_id,
      obra_id: alocacao.obra_id,
      centro_custo_id: alocacao.centro_custo_id,
      categoria_orcamentaria_id: alocacao.categoria_orcamentaria_id,
      competencia: alocacao.competencia,
      percentual_alocacao: alocacao.percentual_alocacao,
      observacoes: alocacao.observacoes || '',
    })
    setErrors({})
    setModal('alocacao')
  }

  const paginatedColaboradores = paginateItems(colaboradores, colaboradoresPage, pageSize)
  const paginatedAlocacoes = paginateItems(alocacoesColaboradores, alocacoesPage, pageSize)
  const paginatedCargos = paginateItems(cargos, cargosPage, pageSize)
  const paginatedFaixas = paginateItems(faixasCusto, faixasPage, pageSize)

  const valor_encargos = colForm.salario_base * (colForm.encargos_mensais / 100)
  const custo_total = colForm.salario_base + valor_encargos + colForm.beneficios_mensais

  function saveCargo() {
    if (!cargoForm.nome.trim()) { setErrors({ nome: 'Nome obrigatório' }); return }
    if (editing) {
      setCargos(prev => prev.map(c => c.id === editing.id ? { ...c, ...cargoForm } : c))
      showToast('Cargo atualizado')
    } else {
      const novo: CargoFuncao = { id: 'cg' + Date.now(), codigo: gerarCodigo(), ...cargoForm, createdAt: new Date().toISOString() }
      setCargos(prev => [...prev, novo])
      addLog('CARGO', novo.id, 'CRIADO', `Cargo ${cargoForm.nome} criado`)
      showToast('Cargo cadastrado')
    }
    setModal(null); setEditing(null); setErrors({})
  }

  function saveFaixa() {
    if (!faixaForm.cargo_id) { setErrors({ cargo_id: 'Cargo obrigatório' }); return }
    if (!faixaForm.descricao.trim()) { setErrors({ descricao: 'Descrição obrigatória' }); return }
    const ct = faixaForm.salario_base_referencia + faixaForm.encargos_referencia + faixaForm.beneficios_referencia
    if (editing) {
      setFaixasCusto(prev => prev.map(f => f.id === editing.id ? { ...f, ...faixaForm, custo_total_referencia: ct } : f))
      showToast('Faixa atualizada')
    } else {
      const nova: FaixaCusto = { id: 'fx' + Date.now(), codigo: gerarCodigo(), ...faixaForm, custo_total_referencia: ct, createdAt: new Date().toISOString() }
      setFaixasCusto(prev => [...prev, nova])
      showToast('Faixa cadastrada')
    }
    setModal(null); setEditing(null); setErrors({})
  }

  function saveColaborador() {
    const e: Record<string, string> = {}
    if (!colForm.nome_completo.trim()) e.nome_completo = 'Nome obrigatório'
    if (!colForm.cpf.trim()) e.cpf = 'CPF obrigatório'
    if (!colForm.data_admissao) e.data_admissao = 'Data de admissão obrigatória'
    if (Object.keys(e).length) { setErrors(e); return }
    if (editing) {
      setColaboradores(prev => prev.map(c => c.id === editing.id ? { ...c, ...colForm, custo_total_mensal: custo_total } : c))
      showToast('Colaborador atualizado')
    } else {
      const novo: Colaborador = { id: 'col' + Date.now(), ...colForm, custo_total_mensal: custo_total, createdAt: new Date().toISOString() }
      setColaboradores(prev => [...prev, novo])
      addLog('COLABORADOR', novo.id, 'CRIADO', `Colaborador ${colForm.nome_completo} criado`)
      showToast('Colaborador cadastrado')
    }
    setModal(null); setEditing(null); setErrors({})
  }

  function saveAlocacao() {
    const e: Record<string, string> = {}
    if (!alocForm.colaborador_id) e.colaborador_id = 'Colaborador obrigatorio'
    if (!alocForm.obra_id) e.obra_id = 'Obra obrigatoria'
    if (!alocForm.centro_custo_id) e.centro_custo_id = 'Centro de custo obrigatorio'
    if (!alocForm.categoria_orcamentaria_id) e.categoria_orcamentaria_id = 'Categoria obrigatoria'
    if (!alocForm.competencia) e.competencia = 'Competencia obrigatoria'
    if (!alocForm.percentual_alocacao || alocForm.percentual_alocacao <= 0 || alocForm.percentual_alocacao > 100) e.percentual_alocacao = 'Percentual deve ficar entre 1 e 100'
    if (Object.keys(e).length) { setErrors(e); return }
    const colaborador = colaboradores.find(c => c.id === alocForm.colaborador_id)
    const valor = (colaborador?.custo_total_mensal || 0) * (alocForm.percentual_alocacao / 100)
    const nova: AlocacaoColaborador = {
      id: editing?.id || 'aloc' + Date.now(),
      ...alocForm,
      valor_alocado: valor,
      createdAt: editing?.createdAt || new Date().toISOString(),
    }
    setAlocacoesColaboradores(prev => editing ? prev.map(item => item.id === editing.id ? nova : item) : [...prev, nova])
    addLog('ALOCACAO_COLABORADOR', nova.id, editing ? 'EDITADO' : 'CRIADO', `Alocacao de ${colaborador?.nome_completo || 'colaborador'} em ${alocForm.competencia} ${editing ? 'atualizada' : 'criada'}`)
    showToast(editing ? 'Alocacao atualizada' : 'Alocacao cadastrada')
    setModal(null); setEditing(null); setErrors({})
  }

  async function deleteCargo(cargo: CargoFuncao, pedirConfirmacao = true) {
    if (pedirConfirmacao) {
      const confirmed = window.confirm(`Excluir o cargo ${cargo.nome}? Esta acao nao pode ser desfeita.`)
      if (!confirmed) return
    }

    try {
      await persistDeletedVersion('cargos_funcoes', cargo as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    setCargos(prev => prev.filter(c => c.id !== cargo.id))
    addLog('CARGO', cargo.id, 'CANCELADO', `Cargo ${cargo.nome} excluído`)
    showToast('Cargo excluído')
  }

  async function deleteFaixa(faixa: FaixaCusto, pedirConfirmacao = true) {
    if (pedirConfirmacao) {
      const confirmed = window.confirm(`Excluir a faixa ${faixa.descricao}? Esta acao nao pode ser desfeita.`)
      if (!confirmed) return
    }

    try {
      await persistDeletedVersion('faixas_custo', faixa as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    setFaixasCusto(prev => prev.filter(f => f.id !== faixa.id))
    addLog('FAIXA_CUSTO', faixa.id, 'CANCELADO', `Faixa ${faixa.descricao} excluída`)
    showToast('Faixa excluída')
  }

  async function deleteColaborador(colaborador: Colaborador, pedirConfirmacao = true) {
    if (pedirConfirmacao) {
      const confirmed = window.confirm(`Excluir o colaborador ${colaborador.nome_completo}? Esta acao nao pode ser desfeita.`)
      if (!confirmed) return
    }

    try {
      await persistDeletedVersion('colaboradores', colaborador as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    setColaboradores(prev => prev.filter(c => c.id !== colaborador.id))
    addLog('COLABORADOR', colaborador.id, 'CANCELADO', `Colaborador ${colaborador.nome_completo} excluído`)
    showToast('Colaborador excluído')
  }

  async function deleteAlocacao(alocacao: AlocacaoColaborador, pedirConfirmacao = true) {
    const colaborador = colaboradores.find(c => c.id === alocacao.colaborador_id)
    if (pedirConfirmacao) {
      const confirmed = window.confirm(`Excluir a alocacao de ${colaborador?.nome_completo || 'colaborador'} em ${alocacao.competencia}? Esta acao nao pode ser desfeita.`)
      if (!confirmed) return
    }

    try {
      await persistDeletedVersion('alocacoes_colaboradores', alocacao as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }
    setAlocacoesColaboradores(prev => prev.filter(a => a.id !== alocacao.id))
    addLog('ALOCACAO_COLABORADOR', alocacao.id, 'CANCELADO', `Alocação de ${colaborador?.nome_completo || 'colaborador'} excluída`)
    showToast('Alocação excluída')
  }


  async function deleteSelectedColaboradores() {
    const selected = colaboradores.filter(item => selectedColaboradores.includes(item.id))
    if (selected.length === 0) return
    if (!window.confirm(`Excluir ${selected.length} colaborador(es) selecionado(s)?`)) return
    for (const item of selected) await deleteColaborador(item, false)
    setSelectedColaboradores([])
  }

  async function deleteSelectedAlocacoes() {
    const selected = alocacoesColaboradores.filter(item => selectedAlocacoes.includes(item.id))
    if (selected.length === 0) return
    if (!window.confirm(`Excluir ${selected.length} alocacao(oes) selecionada(s)?`)) return
    for (const item of selected) await deleteAlocacao(item, false)
    setSelectedAlocacoes([])
  }

  async function deleteSelectedCargos() {
    const selected = cargos.filter(item => selectedCargos.includes(item.id))
    if (selected.length === 0) return
    if (!window.confirm(`Excluir ${selected.length} cargo(s) selecionado(s)?`)) return
    for (const item of selected) await deleteCargo(item, false)
    setSelectedCargos([])
  }

  async function deleteSelectedFaixas() {
    const selected = faixasCusto.filter(item => selectedFaixas.includes(item.id))
    if (selected.length === 0) return
    if (!window.confirm(`Excluir ${selected.length} faixa(s) selecionada(s)?`)) return
    for (const item of selected) await deleteFaixa(item, false)
    setSelectedFaixas([])
  }
  const tabStyle = (t: Tab) => ({
    padding: '7px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: tab === t ? 600 : 400,
    background: tab === t ? '#f0403b' : '#1c1d24',
    color: tab === t ? '#fff' : '#a0a4ab',
    border: '1px solid ' + (tab === t ? '#f0403b' : 'rgba(255,255,255,0.08)'),
  })

  const inp = (err?: string) => ({ width: '100%', background: '#1c1d24', border: `1px solid ${err ? '#f87171' : 'rgba(255,255,255,0.08)'}`, borderRadius: 6, padding: '7px 10px', color: '#f7f8f8', fontSize: 13, outline: 'none', boxSizing: 'border-box' as const })
  const lbl = { color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Mão de Obra</h1>
        <button
          onClick={() => openNewModal(tab === 'cargos' ? 'cargo' : tab === 'faixas' ? 'faixa' : tab === 'alocacoes' ? 'alocacao' : 'colaborador')}
          style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
        >
          + {tab === 'cargos' ? 'Novo Cargo' : tab === 'faixas' ? 'Nova Faixa' : tab === 'alocacoes' ? 'Nova Alocacao' : 'Novo Colaborador'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button style={tabStyle('colaboradores')} onClick={() => setTab('colaboradores')}>Colaboradores ({colaboradores.length})</button>
        <button style={tabStyle('alocacoes')} onClick={() => setTab('alocacoes')}>Alocacoes ({alocacoesColaboradores.length})</button>
        <button style={tabStyle('cargos')} onClick={() => setTab('cargos')}>Cargos ({cargos.length})</button>
        <button style={tabStyle('faixas')} onClick={() => setTab('faixas')}>Faixas de Custo ({faixasCusto.length})</button>
      </div>

      {/* COLABORADORES */}
      {tab === 'colaboradores' && (
        colaboradores.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>ðŸ‘·</div>
            <p style={{ fontSize: 14 }}>Nenhum colaborador cadastrado</p>
            <button onClick={() => openNewModal('colaborador')} style={{ marginTop: 12, background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 16px', color: '#ffb4b1', fontSize: 13, cursor: 'pointer' }}>Cadastrar Colaborador</button>
          </div>
        ) : (
          <>
          <BulkActionsBar selectedCount={selectedColaboradores.length} onClear={() => setSelectedColaboradores([])} onDelete={() => void deleteSelectedColaboradores()} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ width: 36, padding: '8px 12px' }} />
              {['Nome', 'CPF', 'Vínculo', 'Cargo', 'Custo Total/mês', 'Status', 'Ações'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paginatedColaboradores.items.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                  <td style={{ padding: '10px 12px' }}>
                    <input type="checkbox" checked={selectedColaboradores.includes(c.id)} onChange={event => setSelectedColaboradores(prev => event.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} />
                  </td>
                  <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{c.nome_completo}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab', fontFamily: 'monospace' }}>{c.cpf}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{c.tipo_vinculacao}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{c.cargo}</td>
                  <td style={{ padding: '10px 12px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(c.custo_total_mensal)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: c.status === 'ATIVO' ? '#4ade8022' : '#f8717122', color: c.status === 'ATIVO' ? '#4ade80' : '#f87171', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{c.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => openEditColaborador(c)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                    <button onClick={() => deleteColaborador(c)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls
            page={paginatedColaboradores.safePage}
            pageSize={pageSize}
            totalItems={colaboradores.length}
            onPageChange={setColaboradoresPage}
            onPageSizeChange={setPageSize}
          />
          </>
        )
      )}

      {/* ALOCACOES */}
      {tab === 'alocacoes' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: '#62666d', fontSize: 13 }}>
              Total alocado: <span style={{ color: '#4ade80', fontWeight: 700 }}>{formatCurrency(alocacoesColaboradores.reduce((s, a) => s + a.valor_alocado, 0))}</span>
            </div>
          </div>
          {alocacoesColaboradores.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>%</div>
              <p style={{ fontSize: 14 }}>Nenhuma alocacao cadastrada</p>
            </div>
          ) : (
            <>
            <BulkActionsBar selectedCount={selectedAlocacoes.length} onClear={() => setSelectedAlocacoes([])} onDelete={() => void deleteSelectedAlocacoes()} />
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <th style={{ width: 36, padding: '8px 12px' }} />
                {['Colaborador', 'Obra', 'Centro', 'Competencia', '%', 'Valor', 'Ações'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {paginatedAlocacoes.items.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                    <td style={{ padding: '10px 12px' }}>
                      <input type="checkbox" checked={selectedAlocacoes.includes(a.id)} onChange={event => setSelectedAlocacoes(prev => event.target.checked ? [...prev, a.id] : prev.filter(id => id !== a.id))} />
                    </td>
                    <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{colaboradores.find(c => c.id === a.colaborador_id)?.nome_completo || '-'}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{obras.find(o => o.id === a.obra_id)?.nome_obra || '-'}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{centrosCusto.find(c => c.id === a.centro_custo_id)?.nome || '-'}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{a.competencia}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{a.percentual_alocacao}%</td>
                    <td style={{ padding: '10px 12px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(a.valor_alocado)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <button onClick={() => openEditAlocacao(a)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                      <button onClick={() => deleteAlocacao(a)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationControls
              page={paginatedAlocacoes.safePage}
              pageSize={pageSize}
              totalItems={alocacoesColaboradores.length}
              onPageChange={setAlocacoesPage}
              onPageSizeChange={setPageSize}
            />
            </>
          )}
        </div>
      )}

      {/* CARGOS */}
      {tab === 'cargos' && (
        cargos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>ðŸ—‚ï¸</div>
            <p style={{ fontSize: 14 }}>Nenhum cargo cadastrado</p>
          </div>
        ) : (
          <>
          <BulkActionsBar selectedCount={selectedCargos.length} onClear={() => setSelectedCargos([])} onDelete={() => void deleteSelectedCargos()} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ width: 36, padding: '8px 12px' }} />
              {['Código', 'Nome', 'Vínculo Padrão', 'Status', 'Ações'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paginatedCargos.items.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                  <td style={{ padding: '10px 12px' }}>
                    <input type="checkbox" checked={selectedCargos.includes(c.id)} onChange={event => setSelectedCargos(prev => event.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} />
                  </td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab', fontFamily: 'monospace', fontSize: 11 }}>{c.codigo}</td>
                  <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{c.nome}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{c.tipo_vinculacao_padrao}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: c.status === 'ATIVO' ? '#4ade8022' : '#f8717122', color: c.status === 'ATIVO' ? '#4ade80' : '#f87171', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{c.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => openEditCargo(c)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                    <button onClick={() => deleteCargo(c)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls
            page={paginatedCargos.safePage}
            pageSize={pageSize}
            totalItems={cargos.length}
            onPageChange={setCargosPage}
            onPageSizeChange={setPageSize}
          />
          </>
        )
      )}

      {/* FAIXAS */}
      {tab === 'faixas' && (
        faixasCusto.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>ðŸ’°</div>
            <p style={{ fontSize: 14 }}>Nenhuma faixa de custo cadastrada</p>
          </div>
        ) : (
          <>
          <BulkActionsBar selectedCount={selectedFaixas.length} onClear={() => setSelectedFaixas([])} onDelete={() => void deleteSelectedFaixas()} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <th style={{ width: 36, padding: '8px 12px' }} />
              {['Código', 'Descrição', 'Salário Base', 'Encargos', 'Benefícios', 'Custo Total Ref.', 'Status', 'Ações'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paginatedFaixas.items.map(f => (
                <tr key={f.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                  <td style={{ padding: '10px 12px' }}>
                    <input type="checkbox" checked={selectedFaixas.includes(f.id)} onChange={event => setSelectedFaixas(prev => event.target.checked ? [...prev, f.id] : prev.filter(id => id !== f.id))} />
                  </td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab', fontFamily: 'monospace', fontSize: 11 }}>{f.codigo}</td>
                  <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{f.descricao}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{formatCurrency(f.salario_base_referencia)}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{formatCurrency(f.encargos_referencia)}</td>
                  <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{formatCurrency(f.beneficios_referencia)}</td>
                  <td style={{ padding: '10px 12px', color: '#4ade80', fontWeight: 600 }}>{formatCurrency(f.custo_total_referencia)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: f.status === 'ATIVA' ? '#4ade8022' : '#f8717122', color: f.status === 'ATIVA' ? '#4ade80' : '#f87171', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{f.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => openEditFaixa(f)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                    <button onClick={() => deleteFaixa(f)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls
            page={paginatedFaixas.safePage}
            pageSize={pageSize}
            totalItems={faixasCusto.length}
            onPageChange={setFaixasPage}
            onPageSizeChange={setPageSize}
          />
          </>
        )
      )}

      {/* MODAL CARGO */}
      {modal === 'cargo' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 28, width: 420 }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editing ? 'Editar Cargo' : 'Novo Cargo'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={lbl}>Nome *</label>
                <input value={cargoForm.nome} onChange={e => setCargoForm(p => ({ ...p, nome: e.target.value }))} style={inp(errors.nome)} />
                {errors.nome && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.nome}</p>}
              </div>
              <div>
                <label style={lbl}>Descrição</label>
                <input value={cargoForm.descricao} onChange={e => setCargoForm(p => ({ ...p, descricao: e.target.value }))} style={inp()} />
              </div>
              <div>
                <label style={lbl}>Vínculo Padrão</label>
                <select value={cargoForm.tipo_vinculacao_padrao} onChange={e => setCargoForm(p => ({ ...p, tipo_vinculacao_padrao: e.target.value as any }))} style={inp()}>
                  <option value="CLT">CLT</option><option value="PJ">PJ</option><option value="TEMPORARIO">TEMPORÃRIO</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(null)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveCargo} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FAIXA */}
      {modal === 'faixa' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 28, width: 480 }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editing ? 'Editar Faixa de Custo' : 'Nova Faixa de Custo'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Cargo *</label>
                <select value={faixaForm.cargo_id} onChange={e => setFaixaForm(p => ({ ...p, cargo_id: e.target.value }))} style={inp(errors.cargo_id)}>
                  <option value="">Selecione o cargo...</option>
                  {cargos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                {errors.cargo_id && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.cargo_id}</p>}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Descrição *</label>
                <input value={faixaForm.descricao} onChange={e => setFaixaForm(p => ({ ...p, descricao: e.target.value }))} style={inp(errors.descricao)} />
                {errors.descricao && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.descricao}</p>}
              </div>
              {[
                { label: 'Salário Base Ref.', key: 'salario_base_referencia' },
                { label: 'Custo PJ Ref.', key: 'custo_pj_referencia' },
                { label: 'Encargos Ref.', key: 'encargos_referencia' },
                { label: 'Benefícios Ref.', key: 'beneficios_referencia' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label style={lbl}>{label}</label>
                  <input type="number" value={(faixaForm as any)[key]} onChange={e => setFaixaForm(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))} style={inp()} />
                </div>
              ))}
              <div style={{ gridColumn: 'span 2', background: '#1c1d24', borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ color: '#62666d', fontSize: 12 }}>Custo Total de Referência: </span>
                <span style={{ color: '#4ade80', fontWeight: 600, fontSize: 14 }}>{formatCurrency(faixaForm.salario_base_referencia + faixaForm.encargos_referencia + faixaForm.beneficios_referencia)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(null)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveFaixa} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL COLABORADOR */}
      {modal === 'colaborador' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 28, width: 560, maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editing ? 'Editar Colaborador' : 'Novo Colaborador'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Nome Completo *</label>
                <input value={colForm.nome_completo} onChange={e => setColForm(p => ({ ...p, nome_completo: e.target.value }))} style={inp(errors.nome_completo)} />
                {errors.nome_completo && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.nome_completo}</p>}
              </div>
              <div>
                <label style={lbl}>CPF *</label>
                <input value={colForm.cpf} onChange={e => setColForm(p => ({ ...p, cpf: e.target.value }))} style={inp(errors.cpf)} />
                {errors.cpf && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.cpf}</p>}
              </div>
              <div>
                <label style={lbl}>Vínculo</label>
                <select value={colForm.tipo_vinculacao} onChange={e => setColForm(p => ({ ...p, tipo_vinculacao: e.target.value as any }))} style={inp()}>
                  <option value="CLT">CLT</option><option value="PJ">PJ</option><option value="TEMPORARIO">TEMPORÃRIO</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Cargo</label>
                <select value={colForm.cargo_id} onChange={e => { const c = cargos.find(x => x.id === e.target.value); setColForm(p => ({ ...p, cargo_id: e.target.value, cargo: c?.nome || '' })) }} style={inp()}>
                  <option value="">Selecione...</option>
                  {cargos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Data Admissão *</label>
                <input type="date" value={colForm.data_admissao} onChange={e => setColForm(p => ({ ...p, data_admissao: e.target.value }))} style={inp(errors.data_admissao)} />
                {errors.data_admissao && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.data_admissao}</p>}
              </div>
              {[
                { label: 'Salário Base', key: 'salario_base' },
                { label: 'Encargos (%)', key: 'encargos_mensais' },
                { label: 'Benefícios Mensais', key: 'beneficios_mensais' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label style={lbl}>{label}</label>
                  <input type="number" value={(colForm as any)[key]} onChange={e => setColForm(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))} style={inp()} />
                </div>
              ))}
              <div style={{ gridColumn: 'span 2', background: '#1c1d24', borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ color: '#62666d', fontSize: 12 }}>Custo Total Mensal: </span>
                <span style={{ color: '#4ade80', fontWeight: 700, fontSize: 15 }}>{formatCurrency(custo_total)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(null)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveColaborador} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ALOCACAO */}
      {modal === 'alocacao' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(240,64,59,0.35)', borderRadius: 12, padding: 28, width: 520 }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editing ? 'Editar Alocacao' : 'Nova Alocacao'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Colaborador *</label>
                <select value={alocForm.colaborador_id} onChange={e => setAlocForm(p => ({ ...p, colaborador_id: e.target.value }))} style={inp(errors.colaborador_id)}>
                  <option value="">Selecione...</option>
                  {colaboradores.filter(c => c.status === 'ATIVO').map(c => <option key={c.id} value={c.id}>{c.nome_completo} - {formatCurrency(c.custo_total_mensal)}</option>)}
                </select>
                {errors.colaborador_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.colaborador_id}</p>}
              </div>
              <div>
                <label style={lbl}>Obra *</label>
                <select value={alocForm.obra_id} onChange={e => setAlocForm(p => ({ ...p, obra_id: e.target.value, centro_custo_id: '' }))} style={inp(errors.obra_id)}>
                  <option value="">Selecione...</option>
                  {obras.map(o => <option key={o.id} value={o.id}>{o.nome_obra}</option>)}
                </select>
                {errors.obra_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.obra_id}</p>}
              </div>
              <div>
                <label style={lbl}>Centro *</label>
                <select value={alocForm.centro_custo_id} onChange={e => setAlocForm(p => ({ ...p, centro_custo_id: e.target.value }))} style={inp(errors.centro_custo_id)} disabled={!alocForm.obra_id}>
                  <option value="">Selecione...</option>
                  {centrosCusto.filter(c => c.obra_id === alocForm.obra_id).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                {errors.centro_custo_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.centro_custo_id}</p>}
              </div>
              <div>
                <label style={lbl}>Categoria *</label>
                <select value={alocForm.categoria_orcamentaria_id} onChange={e => setAlocForm(p => ({ ...p, categoria_orcamentaria_id: e.target.value }))} style={inp(errors.categoria_orcamentaria_id)}>
                  <option value="">Selecione...</option>
                  {categorias.filter(c => c.grupo_principal === 'MAO_DE_OBRA').map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                {errors.categoria_orcamentaria_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.categoria_orcamentaria_id}</p>}
              </div>
              <div>
                <label style={lbl}>Competencia *</label>
                <input type="month" value={alocForm.competencia} onChange={e => setAlocForm(p => ({ ...p, competencia: e.target.value }))} style={inp(errors.competencia)} />
              </div>
              <div>
                <label style={lbl}>Percentual *</label>
                <input type="number" value={alocForm.percentual_alocacao} onChange={e => setAlocForm(p => ({ ...p, percentual_alocacao: parseFloat(e.target.value) || 0 }))} style={inp(errors.percentual_alocacao)} />
                {errors.percentual_alocacao && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.percentual_alocacao}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(null)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={saveAlocacao} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

